import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Popover from 'material-ui/Popover';
import { withTranslation } from 'react-i18next';
import 'css/selector-dropdown.css';

// Count leaf inspections in a pivot list to decide whether to show the toggle
// and the "All Inspections" row. byInspection items are all flat inspections;
// byAssetType groups nest their inspections (and may repeat one across groups).
function countInspections(items) {
  var list = items || [];
  var n = 0;
  for (var i = 0; i < list.length; i++) {
    var item = list[i];
    if (item.kind === 'inspection') n += 1;
    else if (item.kind === 'assetTypeNode' && item.inspections) n += item.inspections.length;
  }
  return n;
}

// Expansion key for a byAssetType group. The field path hash is shared across
// every value of the same field, so the value is folded in to keep groups
// (e.g. "Water" and "Foam" on the same field) independently collapsible.
function groupKey(node) {
  return (node.assetTypeFieldPathHash || '') + '|' + (node.assetTypeValue || '');
}

// Display an asset-type value prefixed with its field name for context, e.g.
// "Type: Foam". Falls back to the bare value when the field name is missing.
function assetTypeLabel(fieldName, value) {
  var v = value !== null && value !== undefined ? value : '';
  return fieldName ? fieldName + ': ' + v : v;
}

// Flat search across both pivots: inspection titles from the byInspection
// list, then asset-type group labels ("Field: Value") from the byAssetType
// list. Case-insensitive substring match; empty/whitespace query -> no
// results (the caller renders the normal grouped view instead).
export function buildSearchResults(query, byInspection, byAssetType) {
  var q = (query || '').trim().toLowerCase();
  if (!q) return [];

  var results = [];

  var inspections = byInspection || [];
  for (var i = 0; i < inspections.length; i++) {
    var item = inspections[i];
    var title = item.recordsFieldTitle || '';
    if (title.toLowerCase().indexOf(q) !== -1) {
      results.push({
        type: 'inspection',
        inspectionUUID: item.inspectionUUID,
        label: title,
      });
    }
  }

  var groups = byAssetType || [];
  for (var g = 0; g < groups.length; g++) {
    var node = groups[g];
    if (node.kind !== 'assetTypeNode') continue;
    var label = assetTypeLabel(node.assetTypeFieldName, node.assetTypeValue);
    if (label.toLowerCase().indexOf(q) !== -1) {
      results.push({ type: 'assetType', node: node, label: label });
    }
  }

  return results;
}

class InspectDropdown extends Component {
  constructor(props) {
    super(props);
    this.state = {
      expandedGroups: {},
      searchQuery: '',
    };
  }

  componentDidUpdate(prevProps) {
    var justOpened = prevProps.open !== this.props.open && this.props.open;
    var pivotChangedWhileOpen = this.props.open && prevProps.pivot !== this.props.pivot;
    if (justOpened) {
      // Reset expansion to just the selection's ancestor on each open, and
      // clear any search left over from the previous open.
      this.setState({ expandedGroups: this._computeAutoExpand(), searchQuery: '' });
    } else if (pivotChangedWhileOpen) {
      // Preserve manual expansion but also reveal the selection in the new pivot.
      var autoExpand = this._computeAutoExpand();
      this.setState(function (prev) {
        return { expandedGroups: Object.assign({}, prev.expandedGroups, autoExpand) };
      });
    }
  }

  // Groups to auto-expand so the active selection is visible. Keyed per active
  // pivot: byInspection expands the parent inspection (by inspectionUUID);
  // byAssetType expands the matching group (by groupKey).
  _computeAutoExpand = function () {
    var expanded = {};
    var pivot = this.props.pivot === 'byAssetType' ? 'byAssetType' : 'byInspection';
    var selectionMode = this.props.selectionMode;
    var selectionValue = this.props.selectionValue;

    if (pivot === 'byInspection') {
      if (selectionMode === 'inspectionType' && selectionValue && selectionValue.inspectionUUID) {
        var byInspection = this.props.inspectionsByInspection || [];
        for (var i = 0; i < byInspection.length; i++) {
          if (byInspection[i].inspectionUUID === selectionValue.inspectionUUID) {
            expanded[byInspection[i].inspectionUUID] = true;
            break;
          }
        }
      }
      return expanded;
    }

    var byAssetType = this.props.inspectionsByAssetType || [];
    if (selectionMode === 'assetType' && selectionValue && selectionValue.hash) {
      for (var a = 0; a < byAssetType.length; a++) {
        var node = byAssetType[a];
        if (node.kind === 'assetTypeNode' &&
          node.assetTypeFieldPathHash === selectionValue.hash &&
          node.assetTypeValue === selectionValue.value) {
          expanded[groupKey(node)] = true;
        }
      }
    } else if (selectionMode === 'inspectionType' && selectionValue && selectionValue.inspectionUUID) {
      for (var b = 0; b < byAssetType.length; b++) {
        var grp = byAssetType[b];
        if (grp.kind === 'assetTypeNode' && grp.assetTypeValue === selectionValue.assetTypeValue && grp.inspections) {
          for (var c = 0; c < grp.inspections.length; c++) {
            if (grp.inspections[c].inspectionUUID === selectionValue.inspectionUUID) {
              expanded[groupKey(grp)] = true;
              break;
            }
          }
        }
      }
    }
    return expanded;
  }.bind(this);

  toggleGroup = function (key, e) {
    e.stopPropagation();
    var expanded = Object.assign({}, this.state.expandedGroups);
    expanded[key] = !expanded[key];
    this.setState({ expandedGroups: expanded });
  }.bind(this);

  handleSelectAll = function () {
    this.props.onSelectionChange('all', null);
    this.props.onClose();
  }.bind(this);

  handleSelectAssetType = function (node) {
    this.props.onSelectionChange('assetType', {
      hash: node.assetTypeFieldPathHash,
      value: node.assetTypeValue,
    });
    this.props.onClose();
  }.bind(this);

  handleSelectInspection = function (uuid) {
    this.props.onSelectionChange('inspection', uuid);
    this.props.onClose();
  }.bind(this);

  handleSelectInspectionType = function (inspectionUUID, assetTypeValue) {
    this.props.onSelectionChange('inspectionType', {
      inspectionUUID: inspectionUUID,
      assetTypeValue: assetTypeValue,
    });
    this.props.onClose();
  }.bind(this);

  handlePivotChange = function (pivot) {
    if (this.props.onPivotChange) this.props.onPivotChange(pivot);
  }.bind(this);

  handleSearchChange = function (event) {
    this.setState({ searchQuery: event.target.value });
  }.bind(this);

  handleCreateClick = function () {
    this.props.onClose();
    if (this.props.onCreateClick) {
      this.props.onCreateClick();
    }
  }.bind(this);

  // byInspection pivot: each inspection is a flat leaf (selects 'inspection'),
  // unless it spans 2+ asset types, in which case it becomes an expandable
  // parent (label selects the whole inspection; each sub-row selects that
  // inspection scoped to one asset-type value -> 'inspectionType').
  _renderByInspection = function () {
    var props = this.props;
    var state = this.state;
    var items = props.inspectionsByInspection || [];
    var selectionMode = props.selectionMode;
    var selectionValue = props.selectionValue;

    return items.map(function (item) {
      var fanOut = item.assetTypes && item.assetTypes.length >= 2;
      if (fanOut) {
        var isExpanded = state.expandedGroups[item.inspectionUUID];
        var isParentSelected = selectionMode === 'inspection' && selectionValue === item.inspectionUUID;
        var hasSelectedChild = selectionMode === 'inspectionType' &&
          selectionValue && selectionValue.inspectionUUID === item.inspectionUUID;

        return (
          <li key={'insp-' + item.inspectionUUID} className="selector-dropdown-group">
            <div
              className={'selector-dropdown-group-header' + (isParentSelected ? ' selected' : '') + (hasSelectedChild ? ' has-selected-child' : '')}
            >
              <span
                className="selector-dropdown-group-chevron"
                onClick={function (e) { this.toggleGroup(item.inspectionUUID, e); }.bind(this)}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 16, transition: 'transform 300ms ease', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                >
                  chevron_right
                </span>
              </span>
              <span
                className="selector-dropdown-group-label"
                onClick={function () { this.handleSelectInspection(item.inspectionUUID); }.bind(this)}
              >
                {item.recordsFieldTitle}
                <span className="selector-dropdown-group-count">{item.assetTypes.length}</span>
              </span>
            </div>
            {isExpanded && (
              <ul className="selector-dropdown-sublist">
                {item.assetTypes.map(function (at) {
                  var isSelected = selectionMode === 'inspectionType' &&
                    selectionValue &&
                    selectionValue.inspectionUUID === item.inspectionUUID &&
                    selectionValue.assetTypeValue === at.assetTypeValue;
                  return (
                    <li
                      key={item.inspectionUUID + '::' + at.assetTypeValue}
                      className={'selector-dropdown-item selector-dropdown-child' + (isSelected ? ' selected' : '')}
                      onClick={function () { this.handleSelectInspectionType(item.inspectionUUID, at.assetTypeValue); }.bind(this)}
                    >
                      {assetTypeLabel(item.assetTypeFieldName, at.assetTypeValue)}
                    </li>
                  );
                }.bind(this))}
              </ul>
            )}
          </li>
        );
      }

      var isLeafSelected = selectionMode === 'inspection' && selectionValue === item.inspectionUUID;
      return (
        <li
          key={'insp-' + item.inspectionUUID}
          className={'selector-dropdown-item selector-dropdown-ungrouped' + (isLeafSelected ? ' selected' : '')}
          onClick={function () { this.handleSelectInspection(item.inspectionUUID); }.bind(this)}
        >
          {item.recordsFieldTitle}
        </li>
      );
    }.bind(this));
  }.bind(this);

  // byAssetType pivot: assetTypeNode groups (header selects the whole type ->
  // 'assetType'; each nested inspection selects that inspection scoped to the
  // group's type -> 'inspectionType') plus ungrouped inspections as flat leaves.
  _renderByAssetType = function () {
    var props = this.props;
    var state = this.state;
    var items = props.inspectionsByAssetType || [];
    var selectionMode = props.selectionMode;
    var selectionValue = props.selectionValue;

    return items.map(function (item) {
      if (item.kind === 'assetTypeNode') {
        var key = groupKey(item);
        var isGroupSelected = selectionMode === 'assetType' &&
          selectionValue &&
          selectionValue.hash === item.assetTypeFieldPathHash &&
          selectionValue.value === item.assetTypeValue;

        // A group with a single inspection has nothing meaningful to expand into
        // (selecting the group returns the same records as its one inspection), so
        // render it as a non-expandable row -- mirroring the byInspection pivot,
        // where a single-asset-type inspection is a flat leaf.
        if (!item.inspections || item.inspections.length < 2) {
          return (
            <li
              key={'group-' + key}
              className={'selector-dropdown-item selector-dropdown-ungrouped' + (isGroupSelected ? ' selected' : '')}
              onClick={function () { this.handleSelectAssetType(item); }.bind(this)}
            >
              {assetTypeLabel(item.assetTypeFieldName, item.assetTypeValue)}
            </li>
          );
        }

        var isExpanded = state.expandedGroups[key];
        var hasSelectedChild = selectionMode === 'inspectionType' &&
          selectionValue &&
          selectionValue.assetTypeValue === item.assetTypeValue &&
          item.inspections.some(function (insp) {
            return insp.inspectionUUID === selectionValue.inspectionUUID;
          });

        return (
          <li key={'group-' + key} className="selector-dropdown-group">
            <div
              className={'selector-dropdown-group-header' + (isGroupSelected ? ' selected' : '') + (hasSelectedChild ? ' has-selected-child' : '')}
            >
              <span
                className="selector-dropdown-group-chevron"
                onClick={function (e) { this.toggleGroup(key, e); }.bind(this)}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 16, transition: 'transform 300ms ease', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                >
                  chevron_right
                </span>
              </span>
              <span
                className="selector-dropdown-group-label"
                onClick={function () { this.handleSelectAssetType(item); }.bind(this)}
              >
                {assetTypeLabel(item.assetTypeFieldName, item.assetTypeValue)}
                <span className="selector-dropdown-group-count">{item.inspections.length}</span>
              </span>
            </div>
            {isExpanded && (
              <ul className="selector-dropdown-sublist">
                {item.inspections.map(function (insp) {
                  var isSelected = selectionMode === 'inspectionType' &&
                    selectionValue &&
                    selectionValue.inspectionUUID === insp.inspectionUUID &&
                    selectionValue.assetTypeValue === item.assetTypeValue;
                  return (
                    <li
                      key={insp.inspectionUUID + '::' + item.assetTypeValue}
                      className={'selector-dropdown-item selector-dropdown-child' + (isSelected ? ' selected' : '')}
                      onClick={function () { this.handleSelectInspectionType(insp.inspectionUUID, item.assetTypeValue); }.bind(this)}
                    >
                      {insp.recordsFieldTitle}
                    </li>
                  );
                }.bind(this))}
              </ul>
            )}
          </li>
        );
      }

      // kind === 'inspection' (ungrouped) -> flat leaf.
      var isLeafSelected = selectionMode === 'inspection' && selectionValue === item.inspectionUUID;
      return (
        <li
          key={'insp-' + item.inspectionUUID}
          className={'selector-dropdown-item selector-dropdown-ungrouped' + (isLeafSelected ? ' selected' : '')}
          onClick={function () { this.handleSelectInspection(item.inspectionUUID); }.bind(this)}
        >
          {item.recordsFieldTitle}
        </li>
      );
    }.bind(this));
  }.bind(this);

  // Flat search results: inspections then asset-type entries, no grouping.
  // Selection handlers and highlight rules match the grouped views.
  _renderSearchResults = function (results) {
    var selectionMode = this.props.selectionMode;
    var selectionValue = this.props.selectionValue;

    return results.map(function (result) {
      if (result.type === 'inspection') {
        var isInspectionSelected = selectionMode === 'inspection' && selectionValue === result.inspectionUUID;
        return (
          <li
            key={'search-insp-' + result.inspectionUUID}
            className={'selector-dropdown-item selector-dropdown-ungrouped' + (isInspectionSelected ? ' selected' : '')}
            onClick={function () { this.handleSelectInspection(result.inspectionUUID); }.bind(this)}
          >
            {result.label}
          </li>
        );
      }

      var node = result.node;
      var isAssetTypeSelected = selectionMode === 'assetType' &&
        selectionValue &&
        selectionValue.hash === node.assetTypeFieldPathHash &&
        selectionValue.value === node.assetTypeValue;
      return (
        <li
          key={'search-at-' + groupKey(node)}
          className={'selector-dropdown-item selector-dropdown-ungrouped' + (isAssetTypeSelected ? ' selected' : '')}
          onClick={function () { this.handleSelectAssetType(node); }.bind(this)}
        >
          {result.label}
        </li>
      );
    }.bind(this));
  }.bind(this);

  render() {
    var props = this.props;
    var byInspection = props.inspectionsByInspection || [];
    var byAssetType = props.inspectionsByAssetType || [];
    var activePivot = props.pivot === 'byAssetType' ? 'byAssetType' : 'byInspection';
    var inspectionCount = countInspections(byInspection) || countInspections(byAssetType);
    var showAllRow = inspectionCount > 1;
    // The toggle only makes sense when both groupings are non-empty and there is
    // more than one inspection to regroup.
    var showToggle = inspectionCount > 1 && byInspection.length > 0 && byAssetType.length > 0;

    // Render the active pivot, falling back to the other one if the active list
    // is empty (the two pivots hold the same data, so this is a defensive guard
    // for a persisted pivot preference that has no items for this account).
    var pivotToRender = activePivot;
    if (activePivot === 'byAssetType' && byAssetType.length === 0 && byInspection.length > 0) {
      pivotToRender = 'byInspection';
    } else if (activePivot === 'byInspection' && byInspection.length === 0 && byAssetType.length > 0) {
      pivotToRender = 'byAssetType';
    }

    var searchResults = buildSearchResults(this.state.searchQuery, byInspection, byAssetType);
    var isSearching = this.state.searchQuery.trim().length > 0;

    return (
      <Popover
        open={props.open}
        anchorEl={props.anchorEl}
        onRequestClose={props.onClose}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        targetOrigin={{ horizontal: 'left', vertical: 'top' }}
        className="selector-dropdown-popover"
        zDepth={0}
        style={{
          marginTop: 8,
          background: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
          borderRadius: 12,
        }}
      >
        <div className="selector-dropdown">
          <div className="selector-dropdown-header">
            <div className="selector-dropdown-search">
              <span className="material-symbols-outlined selector-dropdown-search-icon">search</span>
              <input
                className="selector-dropdown-search-input"
                type="text"
                placeholder={props.t('inspect:searchInspections')}
                value={this.state.searchQuery}
                onChange={this.handleSearchChange}
              />
            </div>
            <button
              className="selector-create-btn"
              onClick={this.handleCreateClick}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>add</span>
              {props.t('inspect:createInspection')}
            </button>
          </div>

          {showAllRow && !isSearching && (
            <div className="selector-dropdown-pinned">
              <ul className="selector-dropdown-list">
                <li
                  className={'selector-dropdown-item selector-dropdown-all' + (props.selectionMode === 'all' ? ' selected' : '')}
                  onClick={this.handleSelectAll}
                >
                  All Inspections
                </li>
              </ul>
            </div>
          )}

          {showToggle && !isSearching && (
            <div className="selector-dropdown-toggle">
              <div className={'selector-dropdown-toggle-track' + (activePivot === 'byAssetType' ? ' second-active' : '')}>
                <span className="selector-dropdown-toggle-thumb" />
                <button
                  className={'selector-dropdown-toggle-btn' + (activePivot === 'byInspection' ? ' active' : '')}
                  onClick={function () { this.handlePivotChange('byInspection'); }.bind(this)}
                >
                  By Inspection
                </button>
                <button
                  className={'selector-dropdown-toggle-btn' + (activePivot === 'byAssetType' ? ' active' : '')}
                  onClick={function () { this.handlePivotChange('byAssetType'); }.bind(this)}
                >
                  By Asset Type
                </button>
              </div>
            </div>
          )}

          <div className="selector-dropdown-content">
            {isSearching && searchResults.length === 0 ? (
              <div className="selector-dropdown-empty">
                {props.t('inspect:noInspectionsFound')}
              </div>
            ) : (
              <ul className="selector-dropdown-list">
                {isSearching
                  ? this._renderSearchResults(searchResults)
                  : (pivotToRender === 'byInspection' ? this._renderByInspection() : this._renderByAssetType())}
              </ul>
            )}
          </div>
        </div>
      </Popover>
    );
  }
}

InspectDropdown.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  inspectionsByAssetType: PropTypes.array,
  inspectionsByInspection: PropTypes.array,
  pivot: PropTypes.string,
  onPivotChange: PropTypes.func,
  selectionMode: PropTypes.string.isRequired,
  // string uuid for 'inspection'; { inspectionUUID, assetTypeValue } for
  // 'inspectionType'; { hash, value } for 'assetType'; null for 'all'.
  selectionValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onSelectionChange: PropTypes.func.isRequired,
  onCreateClick: PropTypes.func,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(InspectDropdown);
